/**
 * props.ts - Room prop/decoration definitions
 */

export type PropCategory = 'decoration' | 'staging';

export type PropId =
  | 'cobwebs'
  | 'candles'
  | 'blood_pool'
  | 'skull_decor'
  | 'creaky_floor'
  | 'eerie_painting'
  | 'chains'
  | 'dry_ice'
  | 'scent_sprayer'
  | 'strobe_light'
  | 'surround_speaker'
  | 'vibration_floor'
  | 'temp_controller'
  | 'hologram_device'
  | 'auto_lock';

export interface PropStatBonus {
  fear?: number;
  thrill?: number;
  atmosphere?: number;
  fun?: number;
}

export interface PropDef {
  id: PropId;
  nameKey: string;
  descKey: string;
  category: PropCategory;
  maxLevel: number;       // 1-3
  baseCost: number;       // SP cost for level 1
  costMultiplier: number; // cost multiplied per level
  scareBonus: number;     // scare multiplier bonus per level (e.g. 0.05 = +5%)
  statBonus: PropStatBonus; // emotion stat bonuses (per level)
  imagePath: string;      // icon image path
}

export const PROP_DEFS: Record<PropId, PropDef> = {
  // === Decoration Props ===
  cobwebs: {
    id: 'cobwebs',
    nameKey: 'prop_cobwebs',
    descKey: 'prop_cobwebs_desc',
    category: 'decoration',
    maxLevel: 3,
    baseCost: 30,
    costMultiplier: 2.0,
    scareBonus: 0.03,
    statBonus: { fear: 5, atmosphere: 5 },
    imagePath: 'images/props/prop-cobweb.png',
  },
  candles: {
    id: 'candles',
    nameKey: 'prop_candles',
    descKey: 'prop_candles_desc',
    category: 'decoration',
    maxLevel: 3,
    baseCost: 50,
    costMultiplier: 2.0,
    scareBonus: 0.04,
    statBonus: { atmosphere: 10 },
    imagePath: 'images/props/prop-candle.png',
  },
  blood_pool: {
    id: 'blood_pool',
    nameKey: 'prop_blood_pool',
    descKey: 'prop_blood_pool_desc',
    category: 'decoration',
    maxLevel: 3,
    baseCost: 80,
    costMultiplier: 2.5,
    scareBonus: 0.06,
    statBonus: { fear: 10, thrill: 5 },
    imagePath: 'images/props/prop-blood.png',
  },
  skull_decor: {
    id: 'skull_decor',
    nameKey: 'prop_skull_decor',
    descKey: 'prop_skull_decor_desc',
    category: 'decoration',
    maxLevel: 3,
    baseCost: 60,
    costMultiplier: 2.0,
    scareBonus: 0.05,
    statBonus: { fear: 8, atmosphere: 3 },
    imagePath: 'images/props/prop-skull.png',
  },
  creaky_floor: {
    id: 'creaky_floor',
    nameKey: 'prop_creaky_floor',
    descKey: 'prop_creaky_floor_desc',
    category: 'decoration',
    maxLevel: 3,
    baseCost: 40,
    costMultiplier: 2.0,
    scareBonus: 0.03,
    statBonus: { thrill: 5, atmosphere: 3 },
    imagePath: 'images/props/prop-creaky-floor.png',
  },
  eerie_painting: {
    id: 'eerie_painting',
    nameKey: 'prop_eerie_painting',
    descKey: 'prop_eerie_painting_desc',
    category: 'decoration',
    maxLevel: 3,
    baseCost: 100,
    costMultiplier: 2.0,
    scareBonus: 0.05,
    statBonus: { atmosphere: 8, fear: 5 },
    imagePath: 'images/props/prop-painting.png',
  },
  chains: {
    id: 'chains',
    nameKey: 'prop_chains',
    descKey: 'prop_chains_desc',
    category: 'decoration',
    maxLevel: 3,
    baseCost: 70,
    costMultiplier: 2.0,
    scareBonus: 0.04,
    statBonus: { fear: 8, thrill: 5 },
    imagePath: 'images/props/prop-chains.png',
  },

  // === Staging Equipment ===
  dry_ice: {
    id: 'dry_ice',
    nameKey: 'prop_dry_ice',
    descKey: 'prop_dry_ice_desc',
    category: 'staging',
    maxLevel: 3,
    baseCost: 800,
    costMultiplier: 2.5,
    scareBonus: 0.20,
    statBonus: { atmosphere: 20, fear: 5 },
    imagePath: 'images/props/prop-dry-ice.png',
  },
  scent_sprayer: {
    id: 'scent_sprayer',
    nameKey: 'prop_scent_sprayer',
    descKey: 'prop_scent_sprayer_desc',
    category: 'staging',
    maxLevel: 3,
    baseCost: 600,
    costMultiplier: 2.5,
    scareBonus: 0.15,
    statBonus: { atmosphere: 15, thrill: 5 },
    imagePath: 'images/props/prop-scent-sprayer.png',
  },
  strobe_light: {
    id: 'strobe_light',
    nameKey: 'prop_strobe_light',
    descKey: 'prop_strobe_light_desc',
    category: 'staging',
    maxLevel: 3,
    baseCost: 1000,
    costMultiplier: 2.5,
    scareBonus: 0.25,
    statBonus: { fear: 10, thrill: 25 },
    imagePath: 'images/props/prop-strobe-light.png',
  },
  surround_speaker: {
    id: 'surround_speaker',
    nameKey: 'prop_surround_speaker',
    descKey: 'prop_surround_speaker_desc',
    category: 'staging',
    maxLevel: 3,
    baseCost: 1200,
    costMultiplier: 2.5,
    scareBonus: 0.30,
    statBonus: { fear: 15, atmosphere: 20, thrill: 5 },
    imagePath: 'images/props/prop-surround-speaker.png',
  },
  vibration_floor: {
    id: 'vibration_floor',
    nameKey: 'prop_vibration_floor',
    descKey: 'prop_vibration_floor_desc',
    category: 'staging',
    maxLevel: 3,
    baseCost: 900,
    costMultiplier: 2.5,
    scareBonus: 0.20,
    statBonus: { thrill: 20, fear: 10 },
    imagePath: 'images/props/prop-vibration-floor.png',
  },
  temp_controller: {
    id: 'temp_controller',
    nameKey: 'prop_temp_controller',
    descKey: 'prop_temp_controller_desc',
    category: 'staging',
    maxLevel: 3,
    baseCost: 700,
    costMultiplier: 2.5,
    scareBonus: 0.15,
    statBonus: { atmosphere: 15, fear: 5 },
    imagePath: 'images/props/prop-temp-controller.png',
  },
  hologram_device: {
    id: 'hologram_device',
    nameKey: 'prop_hologram_device',
    descKey: 'prop_hologram_device_desc',
    category: 'staging',
    maxLevel: 3,
    baseCost: 2000,
    costMultiplier: 3.0,
    scareBonus: 0.35,
    statBonus: { fear: 20, thrill: 15, fun: 10 },
    imagePath: 'images/props/prop-hologram-device.png',
  },
  auto_lock: {
    id: 'auto_lock',
    nameKey: 'prop_auto_lock',
    descKey: 'prop_auto_lock_desc',
    category: 'staging',
    maxLevel: 3,
    baseCost: 1500,
    costMultiplier: 2.5,
    scareBonus: 0.25,
    statBonus: { thrill: 25, fear: 15 },
    imagePath: 'images/props/prop-auto-lock.png',
  },
};

export const ALL_PROP_IDS = Object.keys(PROP_DEFS) as PropId[];
export const DECORATION_PROP_IDS = ALL_PROP_IDS.filter((id) => PROP_DEFS[id].category === 'decoration');
export const STAGING_PROP_IDS = ALL_PROP_IDS.filter((id) => PROP_DEFS[id].category === 'staging');

export function propCostForLevel(propId: PropId, level: number): number {
  const def = PROP_DEFS[propId];
  if (!def) return 0;
  return Math.floor(def.baseCost * Math.pow(def.costMultiplier, level - 1));
}
